<script setup lang="ts">
/**
 * 邀請碼（批81 需求 11 裁示 17）。
 *
 * 三種碼同一張清單：**客戶碼**（每客戶一組，系統自動產，這裡只能啟停）、
 * **業務碼**與**活動碼**（我方手建）。
 *
 * ★ 路由 `meta.roles: ['admin','superadmin']` ＋ 側欄 nav 的 `roles`——後端是
 *   `require_admin`（operator 打進來 403），前端不藏就是給 operator 一個必然失敗的入口
 *   （批80 踩過一次）。
 */
import { computed, onMounted, ref } from 'vue'

import FilterClearButton from '@/components/FilterClearButton.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { InviteCodeRow } from '@/api/ls'
import { vEnter } from '@/lib/enterKey'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const dialogs = useDialogsStore()

const rows = ref<InviteCodeRow[]>([])
const loading = ref(false)
// 批186：類型過濾記住（前端過濾，清除只要改值）。
const pf = usePersistedFilters('invite-codes', { kindFilter: '' }, { oneOf: { kindFilter: ['', 'customer', 'sales', 'campaign'] } })
const { kindFilter } = pf.refs
const { dirty: filtersDirty } = pf
const clearFilters = () => pf.clear()
/** keyset 續頁游標（批93-D LS-4）；null＝已載完，不畫「載入更多」。 */
const nextCursor = ref<string | null>(null)
const loadingMore = ref(false)

const KIND_LABELS: Record<string, string> = {
  customer: '客戶碼',
  sales: '業務碼',
  campaign: '活動碼',
}
function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind
}

const shown = computed(() =>
  kindFilter.value ? rows.value.filter((r) => r.kind === kindFilter.value) : rows.value,
)

async function load() {
  loading.value = true
  try {
    const page = await ls.listInviteCodes()
    rows.value = page.items
    nextCursor.value = page.next_cursor
  } finally {
    loading.value = false
  }
}
onMounted(load)

/**
 * 續載下一頁（批93-D LS-4）：後端一頁 50 筆，新到舊。
 *
 * ★ 類型桶（客戶碼／業務碼／活動碼）是**前端**算的，所以載的是下一頁**原始資料**、
 *   不是下一頁的過濾結果——與 `LicensesView` 同一個行為。
 */
async function loadMore() {
  if (!nextCursor.value || loadingMore.value) return
  loadingMore.value = true
  try {
    const page = await ls.listInviteCodes({ cursor: nextCursor.value })
    rows.value = [...rows.value, ...page.items]
    nextCursor.value = page.next_cursor
  } catch (e) {
    await fail(e, '讀取下一頁失敗')
  } finally {
    loadingMore.value = false
  }
}

async function fail(e: unknown, fallback: string) {
  const detail =
    e instanceof ApiError && e.payload
      ? (e.payload as { detail?: unknown }).detail
      : undefined
  const msg =
    detail == null
      ? fallback
      : typeof detail === 'string'
        ? detail
        : ((detail as { message?: string }).message ?? JSON.stringify(detail))
  await dialogs.notify({ title: '操作失敗', message: msg })
}

// --- 新增業務／活動碼 ---
const createOpen = ref(false)
const draft = ref<{ kind: 'sales' | 'campaign'; label: string; code: string; note: string }>({
  kind: 'sales',
  label: '',
  code: '',
  note: '',
})
const creating = ref(false)

function openCreate() {
  draft.value = { kind: 'sales', label: '', code: '', note: '' }
  createOpen.value = true
}
async function doCreate() {
  if (!draft.value.label.trim() || creating.value) return
  creating.value = true
  try {
    await ls.createInviteCode({
      kind: draft.value.kind,
      label: draft.value.label.trim(),
      code: draft.value.code.trim() || null,
      note: draft.value.note.trim() || null,
    })
    createOpen.value = false
    await load()
  } catch (e) {
    await fail(e, '建立失敗')
  } finally {
    creating.value = false
  }
}

async function doToggle(row: InviteCodeRow) {
  const next = !row.is_active
  if (!next) {
    // 停用客戶碼有客戶看得到的後果，講清楚再動手。
    const message =
      row.kind === 'customer'
        ? `停用客戶「${row.customer_name ?? '—'}」的邀請碼 ${row.code}？停用後該客戶 CS 後台的邀請卡會消失（下一次心跳生效），已經歸因的推薦紀錄不受影響。`
        : `停用邀請碼 ${row.code}（${row.label ?? '—'}）？停用後客戶就不能再填這組碼。`
    if (!(await dialogs.confirm({ message, confirmText: '停用', danger: true }))) return
  }
  try {
    await ls.toggleInviteCode(row.id, next)
    await load()
  } catch (e) {
    await fail(e, '更新失敗')
  }
}

function fmt(iso: string): string {
  return iso.slice(0, 10)
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>邀請碼</h1>
      <div class="head-acts">
        <select v-model="kindFilter" class="kind-sel">
          <option value="">全部類型</option>
          <option value="customer">客戶碼</option>
          <option value="sales">業務碼</option>
          <option value="campaign">活動碼</option>
        </select>
        <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
        <UiButton variant="primary" @click="openCreate">新增業務／活動碼</UiButton>
      </div>
    </div>

    <div class="card">
      <p class="subtle">
        客戶碼由系統自動產生（每個客戶一組，客戶在 CS 授權頁看得到）；業務碼與活動碼在這裡手動建立。
      </p>
      <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>代碼</th>
              <th>類型</th>
              <th>標籤</th>
              <th>客戶</th>
              <th>被邀請數</th>
              <th>狀態</th>
              <th>建立者</th>
              <th>建立時間</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in shown" :key="r.id">
              <td><code class="mono">{{ r.code }}</code></td>
              <td>
                <span class="status-pill" :class="r.kind === 'customer' ? 'muted' : 'pending'">
                  {{ kindLabel(r.kind) }}
                </span>
              </td>
              <td>{{ r.label ?? '—' }}</td>
              <td>
                <RouterLink v-if="r.customer_id" :to="{ name: 'customers' }" class="lnk">
                  {{ r.customer_name ?? '—' }}
                </RouterLink>
                <span v-else class="subtle">—</span>
              </td>
              <td>{{ r.referral_count }}</td>
              <td>
                <span class="status-pill" :class="r.is_active ? 'active' : 'revoked'">
                  {{ r.is_active ? '啟用中' : '已停用' }}
                </span>
              </td>
              <td class="subtle">{{ r.created_by ?? '系統' }}</td>
              <td class="subtle">{{ fmt(r.created_at) }}</td>
              <td class="acts-cell">
                <div class="acts">
                  <UiButton
                    size="sm"
                    :variant="r.is_active ? 'warn' : 'secondary'"
                    @click="doToggle(r)"
                  >
                    {{ r.is_active ? '停用' : '啟用' }}
                  </UiButton>
                </div>
              </td>
            </tr>
            <tr v-if="!shown.length">
              <td colspan="9" class="subtle empty-row">沒有符合條件的邀請碼。</td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- keyset 分頁：後端一頁 50 筆。`next_cursor` 非 null 才畫這一列。 -->
      <div v-if="nextCursor" class="more">
        <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
        <span class="subtle">已載入 {{ rows.length }} 組</span>
      </div>
    </div>

    <UiModal :open="createOpen" title="新增業務／活動碼" width="480px" @close="createOpen = false">
      <div class="form-grid">
        <label class="field">
          <span class="field-label">類型</span>
          <select v-model="draft.kind">
            <option value="sales">業務碼</option>
            <option value="campaign">活動碼</option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">標籤（必填）</span>
          <input v-model="draft.label" placeholder="例如：業務-王小明／2026 春季展" v-enter="doCreate" />
        </label>
        <label class="field">
          <span class="field-label">代碼（留空＝自動產生 8 碼）</span>
          <input
            v-model="draft.code"
            class="mono"
            placeholder="SPR2026"
            maxlength="16"
            v-enter="doCreate"
          />
          <p class="hint">只能用數字與大寫英文，<strong>不含 I、L、O、U</strong>（避免抄錯），4–16 碼。</p>
        </label>
        <label class="field">
          <span class="field-label">備註</span>
          <textarea v-model="draft.note" rows="2" placeholder="內部備註（選填）" />
        </label>
      </div>
      <template #foot>
        <UiButton @click="createOpen = false">取消</UiButton>
        <UiButton variant="primary" :loading="creating" :disabled="!draft.label.trim()" @click="doCreate">
          建立
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
/* keyset 分頁的「載入更多」列（形狀與 LicensesView 一致） */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.head-acts {
  display: flex;
  align-items: center;
  /* 批186：多了「清除全部條件」，手機寬度要能換行（否則新增鈕會被擠出畫面）。 */
  flex-wrap: wrap;
  gap: var(--sp-2);
}
.kind-sel {
  min-width: 140px;
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.lnk {
  color: var(--brand-600);
  text-decoration: none;
}
.lnk:hover {
  text-decoration: underline;
}
.hint {
  margin: 4px 0 0;
  font-size: var(--fs-11);
  color: var(--fg-4);
  line-height: var(--lh-base);
}
.mono {
  font-family: ui-monospace, monospace;
  letter-spacing: 1px;
}
</style>
